import React, {
  Fragment,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useParams, Link, useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment-timezone";
import ReactModal from "react-modal";
import Select from "react-select";

import "react-json-pretty/themes/monikai.css";

import ai from "../../helpers/axios";
import DynamicAccordionTable from "../reusable-modules/dynamic-accordion-table";
import StateDisplay from "../reusable-modules/state-display";
import TripNotesModule from "./trip-notes-module";
import Loader from "../loader";
import currencyFormatter from "../../helpers/currencyFormatter";
import formatStage from "../../helpers/format-stage";
import { formatTripDate } from "../../helpers/dates";
import MessageContainer from "../messages/message-container";

import GroundTransportationModule from "./ground-transportation-module";
import CateringModule from "./catering-module";
import ClientPayDateModule from "./client-pay-date-module";
import TripHeader from "./trip-header";
import PaymentModule from "./payment-module";
import TripStatusBanner from "./trip-status-banner";
import TripApprovalBanner from "./trip-approval-banner";
import TripApprovalReset from "./trip-approval-reset";
import CancelTripModule from "./cancel-trip-module";
import PassthroughModule from "./passthrough-module";
import DisruptionsTableModule from "./disruptions-table-module";
import {
  TRIP_CANCEL_REASON_OPTIONS,
  TRIP_EDIT_REASON_OPTIONS,
} from "./trip-disruptions-reasons";
import AuditTableModule from "./audit-table-module";
import { LoadingButton } from "../shared/loading-button";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";
import CateringRequestsTableModule from "./catering-requests-table-module";
import PetRequestsTableModule from "./pet-requests-table-module";
import TransferMoneyButton from "./transfer-money-button";
import TransferToLedgerModal from "./transfer-to-ledger-modal";
import UnlinkLedgerModal from "./unlink-ledger-modal";
import MoveFundsModal from "./move-funds-modal";
import RevertFundsModal from "./revert-funds-modal";
import VerifiedByBlock from "./verification/verified-by-block";
import LockedBlock from "./verification/locked-block";
import FundsOnAccountBadge from "./verification/funds-on-account-badge";
import SingleTripQuotes from "./single-trip-quotes";
import VerificationModal from "./verification/verification-modal";
import { isTripLockedForUser } from "../../helpers/is-trip-editable";
import ReferralFeeModule from "./referral-fee-module";
import PassthroughPaymentsModule from "./passthrough-payments-module";
import IncidentalChargesModule from "./incidental-charges-module";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        loading: state.loading || action.payload.loadingStatus,
      };
    }
    case "dataDownloaded": {
      return {
        ...state,
        data: action?.payload?.data,
        loading: false,
        dataDownloadedTimestamp: Date.now(),
        operatorFinancialRecords:
        action?.payload?.data?.operatorFinancialRecords,
      };
    }
    case "rowSelectionEvent": {
      let selectedRows = [...state[action.selectionKey]];
      let records = [...state[action.recordKey]];

      if (action.selected) {
        //get the row from the records by rowId
        let row = records.filter((record) => record._id === action.rowId);

        //add the row to the selectedRows
        selectedRows.push(row[0]);
      } else {
        //remove the row from the selectedRows
        selectedRows = selectedRows.filter(
          (selectedRow) => selectedRow._id !== action.rowId,
        );
      }

      return {
        ...state,
        [action.selectionKey]: selectedRows,
      };
    }

    case "closeOFRModal": {
      return {
        ...state,
        ofrModalControls: {
          ...state.ofrModalControls,
          showModal: false,
        },
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = { ...state.unsavedChanges };

      if (!action.value) {
        delete updatedUnsavedChanges[action.section];
      } else {
        updatedUnsavedChanges[action.section] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges,
      };
    }

    default:
      break;
  }
  return state;
};

const feeCellFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const FEE_VALUE_LABELS = {
  domestic: "US Domestic",
  "225zone": "225mi. Zone",
  usInternational: "US Int.",
  allInternational: "All Int.",
  operatorCollected: "Op. Collected",
  passThrough: "Pass-through",
};

const formatFeeCell = (fee) => {
  const value = fee?.value;
  if (!value) return "-";
  if (value === "custom") {
    return `Custom - ${feeCellFormatter.format(fee?.amount || 0)}`;
  }
  return FEE_VALUE_LABELS[value] || fee?.label || "-";
};

const itineraryColumns = [
  {
    label: "Last Action",
    sort: "asc",
    placement: "standard",
    format: null,
    render: (row) => {
      const lastAction = row.actionLog
        ?.filter(({ action }) =>
          [
            "sendEmail",
            "sendEmailAndSyncToApp",
            "syncToApp",
            "download",
          ].includes(action),
        )
        ?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.[0];

      if (!lastAction) {
        return "N/A";
      }

      const actionsMap = {
        sendEmail: "Email",
        syncToApp: "Sync",
        sendEmailAndSyncToApp: "Email & Sync",
        download: "Download PDF",
      };

      return `${moment(lastAction.timestamp).format("M/D/YY - h:mm a")} (${
        actionsMap[lastAction.action] || ""
      }${
        lastAction.data?.template?.includes("preliminary")
          ? " - Prelim"
          : lastAction.data?.template?.includes("final")
            ? " - Final"
            : lastAction.data?.template?.includes("updated")
              ? " - Updated"
              : ""
      })`;
    },
  },
  {
    label: "Routing",
    sort: "asc",
    format: null,
    render: (row) => {
      if (!row?.legs?.length) {
        return "N/A";
      }

      if (row.legs.length === 1) {
        const leg = row.legs[0];
        return `${formatTripDate(leg.departureDate)} ${
          leg.departureAirport.code
        }-${leg.arrivalAirport.code}`;
      }

      if (
        row.legs.length === 2 &&
        row.legs[0]?.departureAirport.code ===
        row.legs[1]?.arrivalAirport.code &&
        row.legs[1]?.departureAirport.code === row.legs[0]?.arrivalAirport.code
      ) {
        return `${formatTripDate(row.legs[0].departureDate)} - ${formatTripDate(
          row.legs[1].departureDate,
        )} ${
          row.legs[0].departureAirport.code
        }-${row.legs[0].arrivalAirport.code}`;
      }

      return `${formatTripDate(row.legs[0].departureDate)} ${
        row.legs[0].departureAirport.code
      } Multi-Leg`;
    },
    placement: "standard",
  },
  {
    label: "Operator",
    sort: "asc",
    format: null,
    render: (row) => {
      const operators = [
        ...new Set(
          row?.legs?.map(({ operator }) => operator?.name).filter(Boolean),
        ),
      ];

      let result = operators[0];
      if (operators.length > 1) {
        result = `${result} + ${operators.length - 1} more`;
      }

      return result;
    },
    placement: "standard",
  },
  {
    label: "Aircraft",
    sort: "asc",
    format: null,
    render: (row) => {
      const aircrafts = [
        ...new Set(
          row?.legs?.map(({ aircraft }) => aircraft?.name).filter(Boolean),
        ),
      ];

      let result = aircrafts[0];
      if (aircrafts.length > 1) {
        result = `${result} + ${aircrafts.length - 1} more`;
      }

      return result;
    },
    placement: "standard",
  },
  {
    label: "Actions",
    placement: "standard",
    className: "text-align-right",
    render: (row) => {
      return (
        <div className="d-flex justify-content-end">
          <Link
            className="d-block base-button icon-button icon-button-small evo-blue"
            to={`/trips/${row.tripId}/itineraries/${row._id}/preview`}
            title="Preview Itinerary"
          >
            <span className="material-icons">visibility</span>
          </Link>

          <Link
            className="d-block base-button icon-button icon-button-small evo-blue ml-2"
            to={`/trips/${row.tripId}/itineraries/${row._id}`}
            title="Edit Itinerary"
          >
            <span className="material-icons">edit</span>
          </Link>
        </div>
      );
    },
  },
];
const SingleTripContent = () => {
  const { id } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const tab = searchParams.get("tab");
  const expandedOFR = searchParams.get("ofrId");
  const messagesTab = searchParams.get("messagesTab");
  const history = useHistory();

  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdmin = userRoles.some(function (role) {
    return "Super Admin" === role;
  });

  const vp =
    userRoles.some((role) => role === "Account Director") &&
    user?.["https://new.evojets.com/appMetadata"]?.isVP === true;

  const superAdminOrVP =
    userRoles.some(function (role) {
      return "Super Admin" === role;
    }) || vp;

  const isAccountingLedger = user && user["https://new.evojets.com/appMetadata"]?.isAccountingLedger;
  const isAccountingAdmin = superAdmin || (user && user["https://new.evojets.com/appMetadata"]?.isAccounting);
  const isVerificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;

  const initialState = {
    loading: true,
    saving: false,
    data: {
      operatorNotes: [],
      clientNotes: [],
      tripNotes: [],
      catering: [],
      groundTransportationNotes: [],
    },
    refreshDataToken: Date.now(),
    dataDownloadedTimestamp: Date.now(),
    operatorFinancialRecords: [],
    invoiceRecords: [],
    ofrRequestData: {
      notes: "",
      cancelRefundOption: "cancelNoRefund",
    },
    ofrModalControls: {
      showModal: false,
      modalTitle: "",
      buttonText: "Submit",
      showContractSelector: false,
    },
    showDeleteInvoiceModal: false,
    selectedInvoice: null,

    transferToLedgerModal: { open: false, contract: null },
    savingTransferToLedger: false,
    unlinkLedgerModal: { open: false, payment: null },
    savingUnlinkLedger: false,
    moveFundsModal: { open: false, contract: null },
    savingMoveFunds: false,
    revertFundsModal: { open: false, transferGroupId: null, sourceInfo: null, targetInfo: null },
    savingRevertFunds: false,

    verifyModalMode: null,

    unsavedChanges: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const readOnly = isTripLockedForUser(state.data, superAdmin, isVerificationAdmin);

  const setUnsavedChanges = useCallback(({ section, value }) => {
    dispatch({
      type: "unsavedChanges",
      section,
      value,
    });
  }, []);

  useEffect(() => {
    setUnsavedChanges({
      section: "ofrModal",
      value: state?.ofrModalControls?.showModal,
    });
  }, [setUnsavedChanges, state?.ofrModalControls?.showModal]);

  useEffect(() => {
    if (state.operatorFinancialRecords.length && expandedOFR) {
      document
        .getElementById("trip-ofr")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [expandedOFR, state.operatorFinancialRecords]);

  useEffect(() => {
    if (tab && (tab === "messages" || state?.data?._id)) {
      setTimeout(() => {
        document
          .getElementById(messagesTab ? `${messagesTab}-trip-messages` : `trip-${tab}`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });

      }, 300);
    }
  }, [
    tab,
    messagesTab,
    state?.data?._id,
  ]);

  const formatCurrency = (x) => {
    return "$" + x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getData = useCallback(
    async (loadSilently = false) => {
      let loadingStatus = true;
      if (loadSilently) {
        loadingStatus = false;
      }

      dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      let response = await ai
        .auth(token)
        .get(`/api/trips/${id}`)
        .catch((error) => {
          setTimeout(() => {
            toast.error("Unauthorized!", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          }, 50);

          return console.error(error.response);
        });

      if (response?.data && response?.data?.invoiceData) {
        const invoicesForThisTrip = (response?.data?.invoiceData || []).map(
          (invoice) => {
            return {
              ...invoice,
              aircraft: invoice?.tripSummary?.[0]?.aircraft?.name || "N/A",
              totalCharges: currencyFormatter.format(
                invoice?.invoiceDetails.reduce(
                  (acc, charge) => acc + (charge.typeLabel === "refund" ? -charge.amount : charge.amount),
                  0,
                ),
              ),
              createdBy: invoice?.createdBy.name,
              totalPayments: currencyFormatter.format(
                invoice?.paymentDetails.reduce(
                  (acc, payment) => acc + payment.amount,
                  0,
                ),
              ),
            };
          },
        );
        dispatch({
          type: "fieldChanged",
          field: "invoiceRecords",
          value: invoicesForThisTrip,
        });
      }

      if (response) {
        dispatch({
          type: "dataDownloaded",
          payload: {
            data: response.data,
          },
        });
      }
    },
    [id, getAccessTokenSilently, loginWithRedirect],
  );

  const handleTransferMoneyClick = useCallback((contract, option) => {
    if (option === "ledger") {
      dispatch({ type: "fieldChanged", field: "transferToLedgerModal", value: { open: true, contract } });
    } else {
      dispatch({ type: "fieldChanged", field: "moveFundsModal", value: { open: true, contract } });
    }
  }, []);

  const evoContractsColumns = useMemo(() => [
    {
      label: "Last Saved",
      sort: "asc",
      format: null,
      placement: "standard",
      render: (row) => moment(row.updatedAt).format("M/D/YY - h:mm a"),
    },
    {
      label: "Contract #",
      field: "contractTag",
      sort: "asc",
      format: null,
      placement: "standard",
    },
    {
      label: "First Leg",
      sort: "asc",
      format: null,
      render: (row) => `${row?.legs[0]?.departureAirport.code} - ${row?.legs[0]?.arrivalAirport.code}`,
      placement: "standard",
    },
    {
      label: "Last Leg",
      sort: "asc",
      format: null,
      render: (row) => {
        if (row?.legs?.length <= 1) return "-";
        return `${row?.legs[row?.legs?.length - 1]?.departureAirport.code} - ${row?.legs[row?.legs?.length - 1]?.arrivalAirport.code}`;
      },
      placement: "standard",
    },
    {
      label: "Aircraft",
      sort: "asc",
      format: null,
      render: (row) => row?.legs[0]?.aircraft?.name,
      placement: "standard",
    },
    {
      label: "Amount",
      sort: "asc",
      format: "currency",
      render: (row) => row?.pricing?.flightCharges,
      placement: "standard",
    },
    {
      label: "Net Payments",
      field: "netPayments",
      sort: "asc",
      format: "currency",
      placement: "standard",
      isAmountColored: true,
    },
    {
      label: "Stage",
      field: "stage",
      sort: "asc",
      format: "stage",
      placement: "standard",
    },
    {
      label: "Actions",
      placement: "standard",
      className: "text-align-right",
      render: (row) => (
        <div className="d-flex justify-content-end">
          <Link
            className="d-block base-button icon-button icon-button-small evo-blue"
            to={`/trips/${row.tripId}/contracts/${row._id}/preview`}
            title="Preview Contract"
          >
            <span className="material-icons">visibility</span>
          </Link>

          {row.stage !== "signed" && !readOnly && (
            <Link
              className="d-block base-button icon-button icon-button-small evo-blue ml-2"
              to={`/trips/${row.tripId}/contracts/${row._id}`}
              title="Edit Contract"
            >
              <span className="material-icons">edit</span>
            </Link>
          )}

          {isAccountingLedger && !readOnly &&
            <TransferMoneyButton contract={row} onTransferClick={handleTransferMoneyClick}/>}
        </div>
      ),
    },
  ], [handleTransferMoneyClick, isAccountingLedger, readOnly]);

  const handleTransferToLedgerSubmit = useCallback(async (formData) => {
    const token = await getAccessTokenSilently();
    dispatch({ type: "fieldChanged", field: "savingTransferToLedger", value: true });
    try {
      await ai.auth(token).post(`/api/trips/${id}/transfer-to-ledger`, formData);
      dispatch({ type: "fieldChanged", field: "transferToLedgerModal", value: { open: false, contract: null } });
      await getData(true);
      toast.success("Transfer completed successfully.", toastOptions);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error completing transfer"), toastOptions);
    } finally {
      dispatch({ type: "fieldChanged", field: "savingTransferToLedger", value: false });
    }
  }, [getAccessTokenSilently, id, getData]);

  const handleUnlinkLedgerPayment = useCallback((payment) => {
    dispatch({ type: "fieldChanged", field: "unlinkLedgerModal", value: { open: true, payment } });
  }, []);

  const handleUnlinkLedgerSubmit = useCallback(async () => {
    const payment = state.unlinkLedgerModal.payment;
    if (!payment) return;
    const token = await getAccessTokenSilently();
    dispatch({ type: "fieldChanged", field: "savingUnlinkLedger", value: true });
    try {
      await ai.auth(token).delete(`/api/trips/${id}/unlink-ledger-transfer/${payment._id}`);
      dispatch({ type: "fieldChanged", field: "unlinkLedgerModal", value: { open: false, payment: null } });
      await getData(true);
      toast.success("Payment unlinked successfully.", toastOptions);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error unlinking payment"), toastOptions);
    } finally {
      dispatch({ type: "fieldChanged", field: "savingUnlinkLedger", value: false });
    }
  }, [getAccessTokenSilently, id, getData, state.unlinkLedgerModal.payment]);

  const handleMoveFundsSubmit = useCallback(async (formData) => {
    const token = await getAccessTokenSilently();
    dispatch({ type: "fieldChanged", field: "savingMoveFunds", value: true });
    try {
      await ai.auth(token).post(`/api/trips/${id}/move-funds`, formData);
      dispatch({ type: "fieldChanged", field: "moveFundsModal", value: { open: false, contract: null } });
      await getData(true);
      toast.success("Funds moved successfully.", toastOptions);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error moving funds"), toastOptions);
    } finally {
      dispatch({ type: "fieldChanged", field: "savingMoveFunds", value: false });
    }
  }, [getAccessTokenSilently, id, getData]);

  const handleRevertFundsMovement = useCallback(async (payment) => {
    const token = await getAccessTokenSilently();
    const { data } = await ai.auth(token).get(`/api/trips/${id}/funds-movement/${payment.transferGroupId}/info`);
    dispatch({
      type: "fieldChanged",
      field: "revertFundsModal",
      value: {
        open: true,
        transferGroupId: payment.transferGroupId,
        sourceInfo: data.source,
        targetInfo: data.target,
      },
    });
  }, [getAccessTokenSilently, id]);

  const handleRevertFundsSubmit = useCallback(async () => {
    const { transferGroupId } = state.revertFundsModal;
    if (!transferGroupId) return;
    const token = await getAccessTokenSilently();
    dispatch({ type: "fieldChanged", field: "savingRevertFunds", value: true });
    try {
      await ai.auth(token).delete(`/api/trips/${id}/funds-movement/${transferGroupId}/revert`);
      dispatch({
        type: "fieldChanged",
        field: "revertFundsModal",
        value: { open: false, transferGroupId: null, sourceInfo: null, targetInfo: null },
      });
      await getData(true);
      toast.success("Funds movement reverted successfully.", toastOptions);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Error reverting funds movement"), toastOptions);
    } finally {
      dispatch({ type: "fieldChanged", field: "savingRevertFunds", value: false });
    }
  }, [getAccessTokenSilently, getData, id, state.revertFundsModal]);

  const handleDeleteInvoice = useCallback((invoice) => {
    dispatch({
      type: "fieldChanged",
      field: "showDeleteInvoiceModal",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedInvoice",
      value: invoice,
    });
  }, []);

  const handleDeleteInvoiceCancel = () => {
    dispatch({
      type: "fieldChanged",
      field: "showDeleteInvoiceModal",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedInvoice",
      value: null,
    });
  };

  const handleDeleteInvoiceConfirm = useCallback(async () => {
    const token = await getAccessTokenSilently();

    if (!state.selectedInvoice) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    try {
      await ai
        .auth(token)
        .delete(`/api/invoices/${state.selectedInvoice._id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

      await getData(true);

      dispatch({
        type: "fieldChanged",
        field: "showDeleteInvoiceModal",
        value: false,
      });
      dispatch({
        type: "fieldChanged",
        field: "selectedInvoice",
        value: null,
      });
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "saving",
        value: false,
      });
    }
  }, [getAccessTokenSilently, getData, state.selectedInvoice]);

  const handleApprovalDecision = useCallback(
    async (decision, notes) => {
      const token = await getAccessTokenSilently();
      try {
        await ai.auth(token).put(`/api/trips/${id}/${decision}`, { notes });
        await getData(true);
        toast.success(
          decision === "approve" ? "Trip approved" : "Trip declined",
          toastOptions,
        );
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Error updating trip",
          toastOptions,
        );
        throw err;
      }
    },
    [getAccessTokenSilently, getData, id],
  );

  const handleApprovalReset = useCallback(
    async (reason) => {
      const token = await getAccessTokenSilently();
      try {
        await ai.auth(token).put(`/api/trips/${id}/resetReview`, { reason });
        await getData(true);
        toast.success("Approval reset — trip is back in review", toastOptions);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Error resetting approval",
          toastOptions,
        );
        throw err;
      }
    },
    [getAccessTokenSilently, getData, id],
  );

  useEffect(() => {
    getData();
    document.title = "Trip | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.refreshDataToken]);

  const handleOFRModal = useCallback(
    (event) => {
      let action = event?.currentTarget?.name;
      let ofrId = event?.currentTarget?.dataset?.ofrid;

      let actionsRequiringModal = ["edit", "requestSignature", "cancel"];

      let ofrModalControls = {
        ...state.ofrModalControls,
        ofrModalButtonText: "Submit",
        showContractSelector: false,
      };

      let ofrRequestData = {
        ...state.ofrRequestData,
      };

      //get the ofrRecord from operatorFinancialRecords by ofrId
      let ofr = state?.operatorFinancialRecords?.filter(
        (ofr) => ofr._id === event?.currentTarget?.dataset?.ofrid,
      )[0];

      if (action === "edit" && ofr?.stage !== "signed") {
        return {
          requiresModal: false,
          ofrModalControls: ofrModalControls,
          ofrRequestData: ofrRequestData,
        };
      }

      //if only one contract, set contractData to ofrRequestData
      let contractData = {};
      if (state.data?.contractData?.length === 1) {
        contractData = { ...state.data?.contractData[0] };
      }

      if (state.data?.contractData?.length > 1) {
        if (ofr?.associatedContractId) {
          //ofr already has associated contract
          contractData = {
            ...state.data?.contractData?.filter(
              (contract) => contract._id === ofr?.associatedContractId,
            )[0],
          };
        }

        if (!ofr?.associatedContractId) {
          //ofr has no associated contract
          //show contract Selector

          ofrModalControls = {
            ...ofrModalControls,
            showContractSelector: true,
          };
        }
      }

      ofrRequestData = {
        ...ofrRequestData,
        notes: "",
        ofrId: ofrId,
        contract: {
          id: contractData._id,
          client: {
            firstName: contractData?.clientData?.firstName,
            lastName: contractData?.clientData?.lastName,
            isBusiness: !!contractData?.clientData?.isBusiness,
            companyName: contractData?.clientData?.companyName,
            id: contractData?.clientData?._id,
          },
        },
        tripTag: state.data?.tripTag,
        operator: ofr?.operator,
      };

      if (!actionsRequiringModal.includes(action) || !action || !ofrId) {
        ofrModalControls = {
          ...ofrModalControls,
          ofr: ofr,
          associatedContractData: contractData,
          action: action,
          showModal: false,
        };

        dispatch({
          type: "fieldChanged",
          field: "ofrModalControls",
          value: ofrModalControls,
        });

        dispatch({
          type: "fieldChanged",
          field: "ofrRequestData",
          value: ofrRequestData,
        });

        return {
          requiresModal: false,
          ofrModalControls: ofrModalControls,
          ofrRequestData: ofrRequestData,
        };
      }

      if (action === "requestSignature") {
        //show requestSignature

        ofrModalControls = {
          ...ofrModalControls,
          modalTitle: "Request OFR Signature",
          action: action,
          associatedContractData: contractData,
          ofr: ofr,
          showModal: true,
        };
      }

      if (action === "cancel") {
        //show requestSignature

        // TODO
        ofrModalControls = {
          ...ofrModalControls,
          modalTitle: "Cancel OFR",
          modalDescription:
            "You are about to cancel a Signed OFR. Please select the appropriate reason to proceed.",
          action: action,
          associatedContractData: contractData,
          ofr: ofr,
          showModal: true,
        };
      }

      if (action === "edit") {
        ofrModalControls = {
          ...ofrModalControls,
          modalTitle: "Updating Signed OFR",
          modalDescription:
            "You are about to update a Signed OFR. Please select the appropriate reason to proceed.",
          action,
          associatedContractData: contractData,
          ofr,
          showModal: true,
        };
      }

      dispatch({
        type: "fieldChanged",
        field: "ofrModalControls",
        value: ofrModalControls,
      });

      dispatch({
        type: "fieldChanged",
        field: "ofrRequestData",
        value: ofrRequestData,
      });

      return {
        requiresModal: true,
        ofrModalControls: ofrModalControls,
        ofrRequestData: ofrRequestData,
      };
    },
    [
      state.data?.contractData,
      state.data?.tripTag,
      state.ofrModalControls,
      state.ofrRequestData,
      state?.operatorFinancialRecords,
    ],
  );

  const handleOFRActionSubmit = useCallback(
    async (action, ofrRequestData) => {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      let actionData = state?.ofrRequestData;
      let ofrId = state?.ofrRequestData?.ofrId;

      if (ofrRequestData) {
        actionData = ofrRequestData;
        ofrId = ofrRequestData?.ofrId;
      }
      if (
        (action === "cancel" && !actionData.cancellationReason) ||
        (action === "edit" && !actionData.reason)
      ) {
        return toast.error(
          "Please fill in all required fields and retry.",
          toastOptions,
        );
      }
      if (action === "cancel" && state.ofrModalControls?.cancellationReason?.notesRequired && !actionData.cancellationReasonNotes) {
        return toast.error(
          "Please fill in all required fields and retry.",
          toastOptions,
        );
      }

      if (action === "edit") {
        dispatch({
          type: "fieldChanged",
          field: "unsavedChanges",
          value: {},
        });

        history.push({
          pathname: `/trips/${id}/financials/${ofrId}`,
          state: { editReason: actionData.reason },
        });

        return;
      }

      dispatch({
        type: "fieldChanged",
        field: "saving",
        value: true,
      });
      try {
        let response = await ai
          .auth(token)
          .put(`/api/trips/${id}/financials/${ofrId}/action`, {
            action:
              action === "cancel" ? actionData.cancelRefundOption : action,
            actionData: actionData,
          })
          .catch((error) => {
            setTimeout(() => {
              toast.error("Unauthorized!", toastOptions);
            }, 50);

            return console.error(error.response);
          });

        if (response.data && response?.data?.message) {
          toast.success(response?.data?.message, toastOptions);

          await getData(true);

          dispatch({
            type: "fieldChanged",
            field: "ofrModalControls",
            value: {
              ...state.ofrModalControls,
              showModal: false,
            },
          });
        }
      } finally {
        dispatch({
          type: "fieldChanged",
          field: "saving",
          value: false,
        });
      }
    },
    [
      getAccessTokenSilently,
      history,
      id,
      loginWithRedirect,
      state.ofrModalControls,
      state?.ofrRequestData,
      getData,
    ],
  );

  const handleOFRActionButton = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const action = event?.currentTarget?.name;
      const ofrId = event?.currentTarget?.dataset?.ofrid;

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });

      if (action === "view") {
        history.push({
          pathname: `/trips/${id}/financials/${ofrId}`,
          state: { viewMode: true },
        });
        return;
      }

      let modalResponse = handleOFRModal(event);

      if (action === "edit" && !modalResponse?.requiresModal) {
        history.push(`/trips/${id}/financials/${ofrId}`);
        return;
      }

      if (modalResponse?.requiresModal) {
        return;
      }

      const response = await handleOFRActionSubmit(
        action,
        modalResponse?.ofrRequestData,
      );

      return response;
    },
    [id, handleOFRActionSubmit, handleOFRModal, history],
  );

  const handleOFRModalFormSubmission = async (e) => {
    e.preventDefault();

    await handleOFRActionSubmit(state.action);

    return true;
  };

  const handleContractSelectorChange = (selectedContract, action) => {
    let contractData = { ...selectedContract?.contractData };

    dispatch({
      type: "fieldChanged",
      field: "ofrRequestData",
      value: {
        ...state.ofrRequestData,
        contract: {
          id: contractData._id,
          client: {
            firstName: contractData?.clientData?.firstName,
            lastName: contractData?.clientData?.lastName,
            isBusiness: !!contractData?.clientData?.isBusiness,
            companyName: contractData?.clientData?.companyName,
            id: contractData?.clientData?._id,
          },
        },
      },
    });

    dispatch({
      type: "fieldChanged",
      field: "ofrModalControls",
      value: {
        ...state.ofrModalControls,
        associatedContractData: contractData,
      },
    });
  };

  const handleCloseOFRModal = useCallback(() => {
    dispatch({
      type: "closeOFRModal",
    });
  }, []);

  const handleOFREditReasonSelectorChange = useCallback(
    (selection) => {
      dispatch({
        type: "fieldChanged",
        field: "ofrModalControls",
        value: {
          ...state.ofrModalControls,
          reason: selection,
        },
      });

      dispatch({
        type: "fieldChanged",
        field: "ofrRequestData",
        value: {
          ...state.ofrRequestData,
          reason: selection.value,
        },
      });
    },
    [state.ofrModalControls, state.ofrRequestData],
  );

  const operatorFinancialRecordsColumns = useMemo(() => [
      {
        label: "Last Updated",
        sort: "asc",
        placement: "standard",
        format: null,
        render: (row) => {
          return moment(row.updatedAt).format("M/D/YY - h:mm a");
        },
      },
      {
        label: "OFR #",
        field: "ofrTag",
        sort: "asc",
        format: null,
        placement: "standard",
      },
      {
        label: "Operator Name",
        field: "operator.name",
        sort: "asc",
        format: null,
      },
      {
        label: "Aircraft",
        field: "aircraft.name",
        sort: "asc",
        format: null,
        placement: "standard",
      },
      {
        label: "Operator Price",
        field: "operatorPrice",
        sort: "asc",
        format: "currency",
        placement: "standard",
      },
      {
        label: "Total Client Payment",
        field: "totalClientPayment",
        sort: "asc",
        format: "currency",
        placement: "standard",
      },
      {
        label: "Net Payments",
        field: "netPayments",
        sort: "asc",
        format: "currency",
        placement: "standard",
        isAmountColored: true,
      },
      {
        label: "FET",
        sort: "asc",
        format: null,
        placement: "standard",
        render: (row) => formatFeeCell(row?.fet),
      },
      {
        label: "Seg Fees",
        sort: "asc",
        format: null,
        placement: "standard",
        render: (row) => formatFeeCell(row?.segmentFees),
      },
      {
        label: "Stage",
        field: "stage",
        sort: "asc",
        format: "stage",
        placement: "standard",
      },
      {
        label: "Actions",
        placement: "standard",
        className: "text-align-right",
        render: (row) => {
          return (
            <div className="d-flex justify-content-end">
              {["signed", "archived"].includes(row.stage) || readOnly ? (
                <button
                  className="d-block base-button icon-button icon-button-small evo-blue"
                  onClick={handleOFRActionButton}
                  name="view"
                  data-ofrid={row._id}
                  title="View OFR"
                >
                  <span className="material-icons">visibility</span>
                </button>
              ) : (
                <button
                  className="d-block base-button icon-button icon-button-small evo-blue"
                  onClick={handleOFRActionButton}
                  name="edit"
                  data-ofrid={row._id}
                  title="Edit OFR"
                >
                  <span className="material-icons">edit</span>
                </button>
              )}

              {!readOnly && row.stage === "saved" && (
                <button
                  className="d-block base-button icon-button icon-button-small evo-blue ml-2"
                  onClick={handleOFRActionButton}
                  name="requestSignature"
                  data-ofrid={row._id}
                  title="Request Signature"
                >
                  <span className="material-icons">history_edu</span>
                </button>
              )}

              {!readOnly && row.stage === "inReview" && superAdminOrVP && (
                <button
                  className="d-block base-button icon-button icon-button-small evo-dark-green ml-2"
                  onClick={handleOFRActionButton}
                  name="markSigned"
                  data-ofrid={row._id}
                  title="Mark Signed"
                >
                  <span className="material-icons">history_edu</span>
                </button>
              )}

              {!readOnly && row.stage === "signed" && (
                <button
                  className="d-block base-button icon-button icon-button-small btn-danger ml-2"
                  onClick={handleOFRActionButton}
                  name="cancel"
                  data-ofrid={row._id}
                  title="Cancel"
                >
                  <span className="material-icons">cancel</span>
                </button>
              )}

              {!readOnly && row.stage === "inReview" && (
                <button
                  className="d-block base-button icon-button icon-button-small btn-danger ml-2"
                  onClick={handleOFRActionButton}
                  name="revokeSendRequest"
                  data-ofrid={row._id}
                  title="Revoke Send Request"
                >
                  <span className="material-icons">history_edu</span>
                </button>
              )}

              {!readOnly && ["saved", "sent", "inReview"].includes(row.stage) && (
                <button
                  className="d-block base-button icon-button icon-button-small evo-blue ml-2"
                  onClick={handleOFRActionButton}
                  name="archiveOFR"
                  data-ofrid={row._id}
                  title="Archive OFR"
                >
                  <span className="material-icons">archive</span>
                </button>
              )}

              {!readOnly && row.stage === "archived" && (
                <button
                  className="d-block base-button icon-button icon-button-small evo-blue ml-2"
                  onClick={handleOFRActionButton}
                  name="unarchiveOFR"
                  data-ofrid={row._id}
                  title="Restore OFR"
                >
                  <span className="material-icons">unarchive</span>
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [superAdminOrVP, handleOFRActionButton, readOnly],
  );
  const invoiceColumns = useMemo(
    () => [
      {
        label: "Aircraft",
        sort: "asc",
        format: "string",
        placement: "standard",
        render: (row) => {
          const aircrafts = [
            ...new Set(
              row.tripSummary?.map(({ aircraft }) => aircraft?.name).filter(Boolean),
            ),
          ];

          let result = aircrafts[0];
          if (aircrafts.length > 1) {
            result = `${result} + ${aircrafts.length - 1} more`;
          }

          return result;
        },
      },
      {
        label: "Total Charges",
        field: "totalCharges",
        sort: "asc",
        format: "string",
        placement: "standard",
      },
      {
        label: "Total Payments",
        field: "totalPayments",
        sort: "asc",
        format: "string",
        placement: "standard",
      },
      {
        label: "Created By",
        field: "createdBy",
        sort: "asc",
        format: "string",
        placement: "standard",
      },
      {
        label: "Actions",
        placement: "standard",
        className: "text-align-right",
        render: (row) => {
          return (
            <div className="d-flex justify-content-end">
              <Link
                className="d-block base-button icon-button icon-button-small evo-blue"
                to={`/trips/${row.tripId}/invoices/${row._id}/preview`}
                title="Preview Invoice"
              >
                <span className="material-icons">visibility</span>
              </Link>

              {!readOnly && (
                <Link
                  className="d-block base-button icon-button icon-button-small evo-blue ml-2"
                  to={`/trips/${row.tripId}/invoices/${row._id}`}
                  title="Edit Invoice"
                >
                  <span className="material-icons">edit</span>
                </Link>
              )}

              {!readOnly && (
                <button
                  onClick={() => handleDeleteInvoice(row)}
                  className="d-block base-button icon-button icon-button-small btn-danger ml-2"
                  key={`key-row-${row._id}-link`}
                  title="Delete Invoice"
                >
                  <span className="material-icons">delete</span>
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [handleDeleteInvoice, readOnly],
  );

  // When read-only, drop the Edit Itinerary action, keep Preview.
  const itineraryColumnsForRender = useMemo(() => {
    if (!readOnly) return itineraryColumns;
    return itineraryColumns.map((col) =>
      col.label === "Actions"
        ? {
          ...col,
          render: (row) => (
            <div className="d-flex justify-content-end">
              <Link
                className="d-block base-button icon-button icon-button-small evo-blue"
                to={`/trips/${row.tripId}/itineraries/${row._id}/preview`}
                title="Preview Itinerary"
              >
                <span className="material-icons">visibility</span>
              </Link>
            </div>
          ),
        }
        : col,
    );
  }, [readOnly]);

  return (
    <Fragment>
      {!state.loading && <TripStatusBanner stage={state.data?.stage}/>}

      {!state.loading && (
        <TripApprovalBanner
          trip={state.data}
          onDecision={handleApprovalDecision}
          disabled={state.saving}
          canApprove={superAdmin}
        />
      )}

      {!state.loading && (
        <TripApprovalReset
          trip={state.data}
          onReset={handleApprovalReset}
          disabled={state.saving}
          canReset={superAdmin}
        />
      )}

      {!state.loading && (
        <TripHeader
          breadcrumbs={[{ link: "/trips", label: "Trips" }]}
          data={state.data?.headerData}
        />
      )}

      {!state.loading && (
        <div className="tv-status-row mt-20">
          <FundsOnAccountBadge fundsOnAccount={state.data?.headerData?.fundsOnAccount} />

          <div className="tv-status-row-content">
            {state.data?.verified && (
              <VerifiedByBlock
                trip={state.data}
                canUnverify={superAdmin || isVerificationAdmin}
                onUnverify={() =>
                  dispatch({
                    type: "fieldChanged",
                    field: "verifyModalMode",
                    value: "unverify",
                  })
                }
              />
            )}

            {!state.data?.verified && state.data?.lockState?.locked && (
              <LockedBlock
                trip={state.data}
                canUnlock={superAdmin || isVerificationAdmin}
                onUnlock={() =>
                  dispatch({
                    type: "fieldChanged",
                    field: "verifyModalMode",
                    value: "unlock",
                  })
                }
              />
            )}

            {(superAdmin || isVerificationAdmin) &&
              !state.data?.verified &&
              !state.data?.lockState?.locked && (
                <div className="tv-lock-cta-row">
                  <button
                    type="button"
                    className="base-button btn-danger tv-locked-button"
                    onClick={() =>
                      dispatch({
                        type: "fieldChanged",
                        field: "verifyModalMode",
                        value: "lock",
                      })
                    }
                  >
                    Lock Trip
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      <VerificationModal
        isOpen={!!state.verifyModalMode}
        mode={state.verifyModalMode || "unverify"}
        tripId={id}
        onClose={() =>
          dispatch({ type: "fieldChanged", field: "verifyModalMode", value: null })
        }
        onSaved={() =>
          dispatch({
            type: "fieldChanged",
            field: "refreshDataToken",
            value: Date.now(),
          })
        }
      />

      {!state.loading &&
        !state.data?.owner?.id &&
        (state.data?.origin === "clickToFlyBooking" ||
          state.data?.origin === "featuredOneWayBooking" ||
          state.data?.isFeaturedOneWay) && (
          <div className="no-owner-badge mt-20">
            <span className="badge bg-warning">No Owner</span>
          </div>
        )}

      {!state.loading && state?.data?.isPassthrough && (
        <div className="passthrough-badge mt-20">
          <div className="d-flex align-items-center gap-10">
            <span className="badge bg-success">Passthrough</span>
            <PassthroughModule
              mode="edit"
              tripId={id}
              passthroughData={state?.data?.passthroughData}
              parentDispatch={dispatch}
              setUnsavedChanges={setUnsavedChanges}
              readOnly={readOnly}
            />
          </div>
          <div className="mt-10">
            <strong>Operator:</strong>{" "}
            {state?.data?.passthroughData?.operator?.label || "N/A"}
            {" | "}
            <strong>Expected Commission:</strong>{" "}
            {state?.data?.passthroughData?.expectedCommission
              ? formatCurrency(state?.data?.passthroughData?.expectedCommission)
              : "N/A"}
          </div>
        </div>
      )}

      {state.loading && <Loader/>}

      {!state.loading && (
        <Fragment>
          <h5 className="mt-30 mb-1 d-flex">
            Itinerary Data
            {!readOnly && !state.data.itineraryData?.length && (
              <Tooltip
                title={
                  state.operatorFinancialRecords?.length > 0
                    ? "Create Itinerary"
                    : "Create OFR first"
                }
              >
                <div className="d-flex">
                  <Link
                    className="base-button icon-button icon-button-small evo-dark-green ml-2"
                    to={`/trips/${id}/itineraries/new`}
                    disabled={!state.operatorFinancialRecords?.length}
                  >
                    <span className="material-icons">add_circle</span>
                  </Link>
                </div>
              </Tooltip>
            )}
          </h5>

          {state?.data?.itineraryData && !state.loading && (
            <DynamicAccordionTable
              rows={state.data.itineraryData}
              columns={itineraryColumnsForRender}
              context={state.data}
              tableClassName="mt-0 shadow-none"
              key="itinerary-table"
              tableId="trip-itinerary"
              tableName="itinerary-table"
              noItemMessage="No itinerary is created yet."
            />
          )}

          <h5 className="mt-30 mb-1 d-flex">
            Operator Financial Records
            {!readOnly && (
              <Link
                className="base-button icon-button icon-button-small evo-dark-green ml-2"
                to={{
                  pathname: `/trips/${id}/financials/new`,
                  state: state.data,
                }}
                title="Create OFR"
              >
                <span className="material-icons">add_circle</span>
              </Link>
            )}
          </h5>
          <DynamicAccordionTable
            rows={state.operatorFinancialRecords}
            columns={operatorFinancialRecordsColumns}
            tableClassName="mt-0 shadow-none"
            context={state.data}
            tableId="trip-ofr"
            key="ofr-table"
            tableName="ofr-table"
            parentDispatch={dispatch}
            handleActionButton={handleOFRActionButton}
            defaultExpandedRows={[expandedOFR]}
            noItemMessage="No operator financial records are created yet."
          />

          <h5 className="mt-30 mb-1 d-flex">
            EVO Contracts
            {!readOnly && (
              <Link
                className="base-button icon-button icon-button-small evo-dark-green ml-2"
                to={{ pathname: `/trips/${id}/contracts/new`, state: state.data }}
                title="Create Contract"
              >
                <span className="material-icons">add_circle</span>
              </Link>
            )}
          </h5>
          {state?.data?.contractData && (
            <DynamicAccordionTable
              rows={state.data.contractData}
              columns={evoContractsColumns}
              tableClassName="mt-0 shadow-none"
              context={state.data}
              key="contracts-table"
              tableId="trip-contract"
              tableName="contracts-table"
              noItemMessage="No contracts are created yet."
            />
          )}

          <h5 className="mt-30 mb-1 d-flex">
            Invoices
            {!readOnly && !state.data.invoiceData?.length && (
              <Link
                className="base-button icon-button icon-button-small evo-dark-green ml-2"
                to={{
                  pathname: `/trips/${id}/invoices/new`,
                  state: { tripId: id, data: state.data },
                }}
                title="Create Invoice"
              >
                <span className="material-icons">add_circle</span>
              </Link>
            )}
          </h5>
          <DynamicAccordionTable
            rows={state.invoiceRecords}
            columns={invoiceColumns}
            tableClassName="mt-0 shadow-none"
            context={state.data}
            tableName="invoices-table"
            tableId="trip-invoices"
            parentDispatch={dispatch}
            handleActionButton={handleOFRActionButton}
            noItemMessage="No invoices are created yet."
          />

          {!state.loading && (
            <MessageContainer
              className="compact-messages-view"
              id="trip-messages"
              recordId={id}
              recordType="trip"
              superAdmin={superAdmin}
              setUnsavedChanges={setUnsavedChanges}
            />
          )}

          {!state.loading && (
            <SingleTripQuotes tripId={id} leadId={state.data?.leadId} readOnly={readOnly}/>
          )}

          {state.data && (
            <ClientPayDateModule
              tripId={id}
              dataDownloadedTimestamp={state.dataDownloadedTimestamp}
              parentDispatch={dispatch}
              data={state?.data?.clientPayDate}
              key={`key-client-pay-date-${state.dataDownloadedTimestamp.toString()}`}
              setUnsavedChanges={setUnsavedChanges}
              readOnly={readOnly}
            />
          )}

          {state.data && (
            <div className="two-column-grid">
              <ReferralFeeModule
                tripId={id}
                dataDownloadedTimestamp={state.dataDownloadedTimestamp}
                parentDispatch={dispatch}
                data={state?.data?.referralFee || []}
                key={`key-referral-fee-${state.dataDownloadedTimestamp.toString()}`}
                setUnsavedChanges={setUnsavedChanges}
                readOnly={readOnly}
                isAccountingAdmin={isAccountingAdmin}
              />

              <div id="trip-incidentals">
                <IncidentalChargesModule
                  tripId={id}
                  tripTag={state.data?.tripTag}
                  dataDownloadedTimestamp={state.dataDownloadedTimestamp}
                  key={`key-incidental-charges-${state.dataDownloadedTimestamp.toString()}`}
                  readOnly={readOnly}
                  superAdmin={superAdmin}
                  isAccountingAdmin={isAccountingAdmin}
                  isAccountingLedger={isAccountingLedger}
                  isTripOwner={state.data?.owner?.id === user?.sub}
                  onFundsChanged={() =>
                    dispatch({
                      type: "fieldChanged",
                      field: "refreshDataToken",
                      value: Date.now(),
                    })
                  }
                />
              </div>
            </div>
          )}

          <div className="two-column-grid">
            <div className="d-flex flex-column">
              <PaymentModule
                key={`key-client-payment-history-${state.dataDownloadedTimestamp.toString()}`}
                tripId={id}
                tripData={state?.data}
                dataDownloadedTimestamp={state.dataDownloadedTimestamp}
                parentDispatch={dispatch}
                records={state?.data?.clientPaymentHistory || []}
                contracts={state?.data?.contractData || []}
                columns={[
                  {
                    label: "Date",
                    key: "paymentDate",
                  },
                  {
                    label: "Payment Type",
                    key: "paymentType",
                  },
                  {
                    label: "Entered By",
                    key: "enteredBy",
                  },
                  {
                    label: "Contract",
                    key: "contractId",
                  },
                  {
                    label: "Type",
                    key: "transactionType",
                  },
                  {
                    label: "Amount",
                    key: "amount",
                  },
                ]}
                recordType="clientPaymentHistory"
                title="Client Payment History"
                setUnsavedChanges={setUnsavedChanges}
                onUnlinkLedgerPayment={handleUnlinkLedgerPayment}
                onRevertFundsMovement={handleRevertFundsMovement}
                isAccountingLedger={isAccountingLedger}
                readOnly={readOnly}
              />

              <PassthroughPaymentsModule
                tripId={id}
                records={state?.data?.passthroughPayments || []}
                parentDispatch={dispatch}
                dataDownloadedTimestamp={state.dataDownloadedTimestamp}
                canManage={isAccountingAdmin && !readOnly}
              />
            </div>

            <PaymentModule
              key={`key-operator-payment-history-${state.dataDownloadedTimestamp.toString()}`}
              tripId={id}
              tripData={state?.data}
              dataDownloadedTimestamp={state.dataDownloadedTimestamp}
              parentDispatch={dispatch}
              records={state?.data?.operatorPaymentHistory || []}
              operatorFinancialRecords={
                state?.data?.operatorFinancialRecords || []
              }
              columns={[
                {
                  label: "Date",
                  key: "paymentDate",
                },
                {
                  label: "Payment Type",
                  key: "paymentType",
                },
                {
                  label: "Entered By",
                  key: "enteredBy",
                },
                {
                  label: "OFR",
                  key: "ofrId",
                },
                {
                  label: "Type",
                  key: "transactionType",
                },
                {
                  label: "Amount",
                  key: "amount",
                },
              ]}
              recordType="operatorPaymentHistory"
              title="Operator Payment History"
              setUnsavedChanges={setUnsavedChanges}
              readOnly={readOnly}
            />
          </div>

          <CateringRequestsTableModule
            tripData={state?.data}
            readOnly={readOnly}
          />

          <PetRequestsTableModule
            tripData={state?.data}
            readOnly={readOnly}
          />

          <div className="two-column-grid">
            {state.data && (
              <CateringModule
                tripId={id}
                dataDownloadedTimestamp={state.dataDownloadedTimestamp}
                parentDispatch={dispatch}
                catering={state?.data?.catering || []}
                key={`key-catering-${state.dataDownloadedTimestamp.toString()}`}
                setUnsavedChanges={setUnsavedChanges}
                readOnly={readOnly}
              />
            )}

            {state.data && (
              <GroundTransportationModule
                tripId={id}
                dataDownloadedTimestamp={state.dataDownloadedTimestamp}
                parentDispatch={dispatch}
                groundTransportationNotes={
                  state?.data?.groundTransportation || []
                }
                key={`key-trip-ground-notes-${state.dataDownloadedTimestamp.toString()}`}
                setUnsavedChanges={setUnsavedChanges}
                readOnly={readOnly}
              />
            )}

            {state?.data && (
              <TripNotesModule
                tripId={id}
                parentDispatch={dispatch}
                noteType="tripNotes"
                title="Trip"
                notes={state?.data?.tripNotes || []}
                key={`key-trip-notes-${state.dataDownloadedTimestamp.toString()}`}
                setUnsavedChanges={setUnsavedChanges}
                readOnly={readOnly}
              />
            )}
          </div>

          <ReactModal
            isOpen={state?.ofrModalControls?.showModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={handleCloseOFRModal}
            style={{
              overlay: {
                zIndex: 999999,
                backgroundColor: "rgb(32 53 68 / 50%)",
              },
              content: {
                width: "auto",
                border: "none",
                overflowY: "scroll",
                maxHeight: "95vh",
                minWidth: "50vw",
                borderRadius: "0",
                boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
              },
            }}
            appElement={document.getElementById("app")}
          >
            <div className="modal-header edit-note-modal">
              <h4>{state?.ofrModalControls?.modalTitle}</h4>
            </div>
            <div className="modal-body edit-note-modal modal-body-with-scroll">
              <form
                onSubmit={handleOFRModalFormSubmission}
                className="w-100 base-form mt-20 tight-paragraphs"
              >
                {state.ofrModalControls?.action === "cancel" && (
                  <React.Fragment>
                    <FormControl className="form-group base-radio-group">
                      <label id="cancel-refund-option">
                        Eligible for Refund?
                      </label>

                      <RadioGroup
                        aria-labelledby="cancel-refund-option"
                        name="cancelRefundOption"
                        value={state.ofrRequestData?.cancelRefundOption}
                        onChange={(e) => {
                          dispatch({
                            type: "fieldChanged",
                            field: "ofrRequestData",
                            value: {
                              ...state.ofrRequestData,
                              cancelRefundOption: e.target.value,
                            },
                          });
                        }}
                      >
                        <FormControlLabel
                          value="cancelNoRefund"
                          control={<Radio/>}
                          label="No"
                        />
                        <FormControlLabel
                          value="cancelYesRefund"
                          control={<Radio/>}
                          label="Yes"
                        />
                      </RadioGroup>
                    </FormControl>
                  </React.Fragment>
                )}

                {state?.ofrModalControls?.modalDescription && (
                  <div className="w-100 mt-20 mb-20">
                    {state?.ofrModalControls?.modalDescription}
                  </div>
                )}

                {state?.action !== "edit" && state?.ofrModalControls?.ofr && (
                  <div>
                    <h5>OFR #{state?.ofrModalControls?.ofr?.ofrTag}</h5>
                    <ul>
                      <li>
                        <strong>Operator Name:</strong>{" "}
                        {state?.ofrModalControls?.ofr?.operator?.name}
                      </li>
                      <li>
                        <strong>Operator Price:</strong>{" "}
                        {currencyFormatter.format(
                          state?.ofrModalControls?.ofr?.operatorPrice,
                        )}
                      </li>
                    </ul>

                    {state?.ofrModalControls?.showContractSelector && (
                      <div className="form-group">
                        <label>Associated Contract</label>
                        <Select
                          className="base-select yom-select"
                          isClearable
                          isSearchable
                          placeholder={"Select a Contract"}
                          name="associatedContract"
                          onChange={handleContractSelectorChange}
                          options={state?.data?.contractData?.map(
                            (contract) => {
                              //depart airport code of each leg, separated by -

                              let routing = contract?.legs?.map(
                                (leg) => leg?.departureAirport?.code,
                              );

                              routing = routing.join("-");

                              //updated at, eastern time, like June 1, 2023 2:00 PM

                              let updatedAt = moment(
                                contract?.updatedAt,
                              ).format("MMM D, YYYY h:mm A");

                              let stage = formatStage(contract?.stage);

                              return {
                                label: `#${contract.contractTag} | ${contract.clientData.firstName} ${contract.clientData.lastName} | ${routing} | Updated: ${updatedAt} | ${stage}`,
                                value: contract._id,
                                contractData: contract,
                              };
                            },
                          )}
                        ></Select>
                      </div>
                    )}

                    <h6>Trip & Contract Data</h6>

                    <ul>
                      <li>
                        <strong>Trip ID: #</strong>
                        {state.ofrRequestData?.tripTag}
                      </li>
                      <li>
                        <strong>Contract ID: </strong>
                        {state.ofrModalControls?.associatedContractData
                          ?.contractTag
                          ? `#${state.ofrModalControls?.associatedContractData?.contractTag}`
                          : "Select an associated contract."}
                      </li>

                      <li>
                        <strong>Client: </strong>

                        {!state.ofrModalControls?.associatedContractData._id &&
                          `Select an associated contract.`}

                        {state.ofrModalControls?.associatedContractData?._id &&
                          state.ofrRequestData?.contract?.client?.isBusiness &&
                          `${state.ofrRequestData?.contract?.client?.companyName}`}
                        {state.ofrModalControls?.associatedContractData?._id &&
                          !state.ofrRequestData?.contract?.client?.isBusiness &&
                          `${state.ofrRequestData?.contract?.client?.firstName} ${state.ofrRequestData?.contract?.client?.lastName}`}
                      </li>

                      <li>
                        <strong>Trip Legs: </strong>{" "}
                        {state.ofrModalControls?.ofr?.whichLeg === 1
                          ? "All"
                          : state.ofrModalControls?.ofr?.whichLeg === 2
                            ? "Partial"
                            : "Please define this on the OFR record before requesting a signature."}
                      </li>
                    </ul>

                    <h6>Cancellation Policy</h6>
                    <ul>
                      {!!state.ofrModalControls?.ofr
                          ?.operatorCancellationPolicies?.length &&
                        state.ofrModalControls?.ofr?.operatorCancellationPolicies.map(
                          (penalty) => {
                            return (
                              <li key={penalty._id}>
                                <strong>
                                  {penalty.type === 1 && `${penalty.value}%`}

                                  {penalty.type === 2 &&
                                    currencyFormatter.format(penalty.value)}
                                </strong>{" "}
                                on{" "}
                                {moment(penalty.dueDate).format("MMM D, YYYY")}{" "}
                                <em>
                                  {`(`}
                                  {moment(penalty.dueDate).fromNow()}
                                  {`)`}
                                </em>
                              </li>
                            );
                          },
                        )}
                    </ul>
                  </div>
                )}

                {state.ofrModalControls?.action === "cancel" && (
                  <div className="form-group">
                    <label>
                      Reason <span className="required">*</span>
                    </label>
                    <Select
                      className="base-select yom-select"
                      isClearable
                      isSearchable
                      placeholder={"Select a Reason"}
                      name="cancellationReason"
                      onChange={(selection) => {
                        dispatch({
                          type: "fieldChanged",
                          field: "ofrModalControls",
                          value: {
                            ...state.ofrModalControls,
                            cancellationReason: selection,
                          },
                        });

                        dispatch({
                          type: "fieldChanged",
                          field: "ofrRequestData",
                          value: {
                            ...state.ofrRequestData,
                            cancellationReason: selection?.value,
                          },
                        });
                      }}
                      options={TRIP_CANCEL_REASON_OPTIONS}
                      menuPosition="fixed"
                    />
                  </div>
                )}

                {state.ofrModalControls?.action === "cancel" &&
                  state.ofrModalControls?.cancellationReason?.notesRequired && (
                    <div className="form-group">
                      <label className="base-label" htmlFor="note">
                        Cancellation Reason Notes{" "}
                        <span className="required">*</span>
                      </label>
                      <textarea
                        name="cancellationReasonNotes"
                        id="cancellationReasonNotes"
                        cols="30"
                        rows="3"
                        value={
                          state?.ofrRequestData?.cancellationReasonNotes || ""
                        }
                        onChange={(e) => {
                          dispatch({
                            type: "fieldChanged",
                            field: "ofrRequestData",
                            value: {
                              ...state.ofrRequestData,
                              cancellationReasonNotes: e.target.value,
                            },
                          });
                        }}
                        required
                        placeholder={`Please clarify the situation.`}
                      />
                    </div>
                  )}

                {state.ofrRequestData?.cancelRefundOption ===
                  "cancelYesRefund" && (
                    <React.Fragment>
                      <FormControl className="form-group base-radio-group">
                        <label id="refund-label">Refund Details</label>

                        <RadioGroup
                          aria-labelledby="refund-label"
                          name="refundDetails"
                          value={state.ofrRequestData?.refundDetails}
                          onChange={(e) => {
                            dispatch({
                              type: "fieldChanged",
                              field: "ofrRequestData",
                              value: {
                                ...state.ofrRequestData,
                                refundDetails: e.target.value,
                              },
                            });
                          }}
                        >
                          <FormControlLabel
                            value="Full"
                            control={<Radio/>}
                            label="Full"
                          />
                          <FormControlLabel
                            value="Partial"
                            control={<Radio/>}
                            label="Partial"
                          />
                          <FormControlLabel
                            value="TBD"
                            control={<Radio/>}
                            label="TBD"
                          />
                          <FormControlLabel
                            value="Credit"
                            control={<Radio/>}
                            label="Credit"
                          />
                        </RadioGroup>
                      </FormControl>

                      {state.ofrRequestData?.refundDetails &&
                        ["Partial", "TBD", "Credit"].includes(
                          state.ofrRequestData.refundDetails,
                        ) && (
                          <div className="form-group">
                            <label className="base-label" htmlFor="note">
                              Operator Refund Notes
                            </label>
                            <textarea
                              name="operatorRefundNotes"
                              id="operatorRefundNotes"
                              cols="30"
                              rows="3"
                              value={
                                state?.ofrRequestData?.operatorRefundNotes || ""
                              }
                              onChange={(e) => {
                                dispatch({
                                  type: "fieldChanged",
                                  field: "ofrRequestData",
                                  value: {
                                    ...state.ofrRequestData,
                                    operatorRefundNotes: e.target.value,
                                  },
                                });
                              }}
                              required
                              placeholder={`Please clarify the refund details.`}
                            ></textarea>
                          </div>
                        )}
                    </React.Fragment>
                  )}

                {(state.ofrModalControls?.action === "requestSignature" ||
                    state.ofrModalControls?.action === "cancel") &&
                  state.ofrModalControls?.ofr?.whichLeg === 2 && (
                    <React.Fragment>
                      <div className="form-group">
                        <label className="base-label" htmlFor="note">
                          Partial OFR Notes <span className="required">*</span>
                        </label>
                        <textarea
                          name="partialOFRNotes"
                          id="partialOFRNotes"
                          cols="30"
                          rows="3"
                          value={state?.ofrRequestData?.partialOFRNotes || ""}
                          onChange={(e) => {
                            dispatch({
                              type: "fieldChanged",
                              field: "ofrRequestData",
                              value: {
                                ...state.ofrRequestData,
                                partialOFRNotes: e.target.value,
                              },
                            });
                          }}
                          required={state.ofrModalControls?.ofr?.whichLeg === 2}
                          placeholder={
                            state.ofrModalControls?.ofr?.whichLeg === 2
                              ? "Please enter a reason for requesting a partial OFR."
                              : ""
                          }
                        ></textarea>
                      </div>
                    </React.Fragment>
                  )}

                {(state.ofrModalControls?.action === "requestSignature" ||
                  state.ofrModalControls?.action === "cancel") && (
                  <React.Fragment>
                    <div className="form-group">
                      <label className="base-label" htmlFor="note">
                        Request Notes
                      </label>
                      <textarea
                        name="notes"
                        id="notes"
                        cols="30"
                        rows="3"
                        value={state?.ofrRequestData?.notes || ""}
                        onChange={(e) => {
                          dispatch({
                            type: "fieldChanged",
                            field: "ofrRequestData",
                            value: {
                              ...state.ofrRequestData,
                              notes: e.target.value,
                            },
                          });
                        }}
                        placeholder={`Additional request notes.`}
                      ></textarea>
                    </div>
                  </React.Fragment>
                )}

                {state?.action === "edit" && (
                  <div className="form-group">
                    <label>
                      Reason <span className="required">*</span>
                    </label>

                    <Select
                      className="base-select yom-select"
                      isClearable
                      isSearchable
                      placeholder="Select a Reason"
                      name="reason"
                      onChange={handleOFREditReasonSelectorChange}
                      options={TRIP_EDIT_REASON_OPTIONS}
                      menuPosition="fixed"
                    />
                  </div>
                )}
              </form>
            </div>

            <div className="modal-footer w-100 mt-10">
              {(state?.action === "edit" ||
                state.ofrModalControls?.ofr?.whichLeg) && (
                <div className="w-100 mt-20 d-flex gap-20">
                  <LoadingButton
                    className="base-button evo-blue flex-half"
                    type="submit"
                    isLoading={state.saving}
                    onClick={handleOFRModalFormSubmission}
                  >
                    {state.ofrModalControls?.modalButtonText
                      ? state.ofrModalControls?.modalButtonText
                      : "Submit"}
                  </LoadingButton>

                  <button
                    className="base-button btn-danger flex-half"
                    type="button"
                    onClick={handleCloseOFRModal}
                    disabled={state.saving}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {state?.action !== "edit" &&
                !state.ofrModalControls?.ofr?.whichLeg && (
                  <p className="w-100 text-center">
                    <strong>
                      You must associate your OFR with legs before you can
                      submit.
                    </strong>
                  </p>
                )}
            </div>
          </ReactModal>

          <DisruptionsTableModule
            tripId={id}
            tripData={state?.data}
            parentDispatch={dispatch}
            setUnsavedChanges={setUnsavedChanges}
            readOnly={readOnly}
          />

          <AuditTableModule refreshDataToken={state.refreshDataToken}/>

          <CancelTripModule
            tripId={id}
            parentDispatch={dispatch}
            tripData={state?.data}
            setUnsavedChanges={setUnsavedChanges}
            readOnly={readOnly}
          />

          {!state.loading && !state?.data?.isPassthrough && !readOnly && (
            <PassthroughModule
              tripId={id}
              parentDispatch={dispatch}
              setUnsavedChanges={setUnsavedChanges}
            />
          )}

          <StateDisplay user={user} data={state}/>

          <ReactModal
            isOpen={state.showDeleteInvoiceModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={handleDeleteInvoiceCancel}
            className="trip-notes-module-modal"
            style={{
              overlay: {
                zIndex: 999999,
                backgroundColor: "rgb(32 53 68 / 50%)",
              },
              content: {
                width: "auto",
                padding: "40px",
                border: "none",
                maxWidth: "700px",
                maxHeight: "50%",
                borderRadius: "0",
                boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
              },
            }}
            appElement={document.getElementById("app")}
          >
            <div className="modal-header base-modal">
              <h4>Delete invoice</h4>
            </div>

            <div className="modal-body base-modal overflow-scroll">
              <div className="w-100 mt-20">
                This {state.title?.toLowerCase() || ""} record will be
                permanently deleted from the trip. Are you sure you want to
                continue?
              </div>

              <div className="w-100 mt-20 d-flex gap-20">
                <button
                  className="base-button evo-blue flex-half"
                  type="button"
                  onClick={handleDeleteInvoiceCancel}
                  disabled={state.saving}
                >
                  Cancel
                </button>

                <LoadingButton
                  className="base-button btn-danger flex-half"
                  type="button"
                  onClick={handleDeleteInvoiceConfirm}
                  isLoading={state.saving}
                >
                  Delete
                </LoadingButton>
              </div>
            </div>
          </ReactModal>
        </Fragment>
      )}
      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>

      <TransferToLedgerModal
        open={state.transferToLedgerModal.open}
        contract={state.transferToLedgerModal.contract}
        onSubmit={handleTransferToLedgerSubmit}
        onClose={() => dispatch({
          type: "fieldChanged",
          field: "transferToLedgerModal",
          value: { open: false, contract: null },
        })}
        saving={state.savingTransferToLedger}
      />

      <UnlinkLedgerModal
        open={state.unlinkLedgerModal.open}
        onSubmit={handleUnlinkLedgerSubmit}
        onClose={() => dispatch({
          type: "fieldChanged",
          field: "unlinkLedgerModal",
          value: { open: false, payment: null },
        })}
        saving={state.savingUnlinkLedger}
      />

      <MoveFundsModal
        open={state.moveFundsModal.open}
        sourceTrip={state?.data}
        sourceContract={state.moveFundsModal.contract}
        onSubmit={handleMoveFundsSubmit}
        onClose={() => dispatch({
          type: "fieldChanged",
          field: "moveFundsModal",
          value: { open: false, contract: null },
        })}
        saving={state.savingMoveFunds}
      />

      <RevertFundsModal
        open={state.revertFundsModal.open}
        sourceInfo={state.revertFundsModal.sourceInfo}
        targetInfo={state.revertFundsModal.targetInfo}
        onSubmit={handleRevertFundsSubmit}
        onClose={() => dispatch({
          type: "fieldChanged",
          field: "revertFundsModal",
          value: { open: false, transferGroupId: null, sourceInfo: null, targetInfo: null },
        })}
        saving={state.savingRevertFunds}
      />
    </Fragment>
  );
};

export default SingleTripContent;
